import { wrapRequest } from '@applandeo/api-wrapper';
import { FormikProps, useFormik } from 'formik';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { createOwnRequest, createUserRequest, getRequestPreview } from '@/dataExchange/services/newRequest.service';
import { AbsenceTypeResponseDto } from '@/domain/myOrganization';
import { RequestNoRestrictionSummary, RequestWithRestrictionSummary } from '@/domain/newRequest';
import { DatePickerRange } from '@/domain/requestsPage';
import { useRequestsConverter } from '@/hooks/useRequests/useRequestsConverter';
import useUsedLimits from '@/hooks/useUsedLimits/useUsedLimits';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';
import useQueriedUserData from '@/pages/NewRequest/useQueriedUserData';
import { useSentryActions } from '@/sentry/hooks/useSentryActions';
import { SENTRY_ACTIONS } from '@/sentry/sentryActions';
import { normalizeDateForAPI } from '@/utils/date/date';

export enum NEW_REQUEST_FIELD {
    selectedUser = 'selectedUser',
    selectedRequestType = 'selectedRequestType',
    dateRange = 'dateRange',
    comment = 'comment',
}

export interface UserOption {
    label: string;
    userMail: string;
    userId: string;
}

export interface NewRequestFields {
    [NEW_REQUEST_FIELD.selectedUser]?: UserOption;
    [NEW_REQUEST_FIELD.selectedRequestType]?: AbsenceTypeResponseDto;
    [NEW_REQUEST_FIELD.dateRange]: DatePickerRange;
    [NEW_REQUEST_FIELD.comment]: string;
}

const INITIAL_VALUES: NewRequestFields = {
    selectedUser: { label: '', userMail: '', userId: '' },
    selectedRequestType: undefined,
    dateRange: { startDate: null, endDate: null },
    comment: '',
};

const useNewRequestLogic = () => {
    const createOwnRequestWrapper = wrapRequest(createOwnRequest);
    const createUserRequestWrapper = wrapRequest(createUserRequest);
    const getRequestPreviewWrapper = wrapRequest(getRequestPreview);

    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();
    const { isLead: isCurrentUserLead, credentialId: currentUserId } = useLoggedInUserData();
    const location = useLocation();
    const { queriedCredentialId, queriedUsername, queriedUserFullName } = useQueriedUserData();
    const { convertRequestPreviewResponseDtoToRequestSummary } = useRequestsConverter();
    const { handleSentryAction } = useSentryActions();

    const [requestSummary, setRequestSummary] = useState<RequestNoRestrictionSummary & RequestWithRestrictionSummary>();
    const [reloadDataOnReset, setReloadDataOnReset] = useState<[]>([]);

    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const [isCreatedSuccess, setIsCreatedSuccess] = useState(false);

    const createOwnAbsence = useCallback(
        (formValues: NewRequestFields) => {
            const dto = prepareRequestBody(formValues);
            if (!dto) return;

            createOwnRequestWrapper
                .fetch(dto)
                .onAnyError(error => {
                    createAlert(ALERT_VARIANT.error, t('newRequest_alerts_failed_to_create'));
                    handleSentryAction(error, SENTRY_ACTIONS.ERROR_CREATE_OWN_REQUEST);
                })
                .onSuccess(() => {
                    setIsCreatedSuccess(true);
                    getAllLimits();
                    getRequestSummary();
                    mixpanelTracking(MIXPANEL_ACTIONS.NEW_REQUEST, {
                        absenceName: formValues.selectedRequestType?.name,
                        isManager: !!isCurrentUserLead,
                    });
                });
        },
        [createAlert, createOwnRequestWrapper, t]
    );

    const createUserAbsence = useCallback(
        (formValues: NewRequestFields) => {
            const dto = prepareRequestBody(formValues);
            if (!dto) return;

            createUserRequestWrapper
                .fetch({ userId: formValues?.selectedUser?.userId, userRequestBody: dto })
                .onAnyError(error => {
                    createAlert(ALERT_VARIANT.error, t('newRequest_alerts_failed_to_create'));
                    handleSentryAction(error, SENTRY_ACTIONS.ERROR_CREATE_USER_REQUEST);
                })
                .onSuccess(() => {
                    setIsCreatedSuccess(true);
                    getAllLimits();
                    getRequestSummary();
                    mixpanelTracking(MIXPANEL_ACTIONS.NEW_REQUEST, {
                        absenceName: formValues.selectedRequestType?.name,
                        isManager: !!isCurrentUserLead,
                    });
                });
        },
        [createAlert, createUserRequestWrapper, t]
    );

    const newRequestFormik: FormikProps<NewRequestFields> = useFormik({
        initialValues: INITIAL_VALUES,
        validationSchema: Yup.object().shape({
            selectedRequestType: Yup.object().required(t('filterView_absenceTypePicker_title')),
            dateRange: Yup.object().test(
                'isRequired',
                t('newApplication_applicationDate_chooseDays_title'),
                dateRange => {
                    return !(!dateRange?.startDate || !dateRange?.endDate);
                }
            ),
        }),
        onSubmit: (formValues: NewRequestFields) => {
            const selectedUserId = formValues?.[NEW_REQUEST_FIELD.selectedUser]?.userId;
            const isOwnRequest = selectedUserId === currentUserId || !isCurrentUserLead;

            isOwnRequest ? createOwnAbsence(formValues) : createUserAbsence(formValues);
        },
    });

    useEffect(() => {
        if (!queriedCredentialId || !queriedUserFullName) return;
        newRequestFormik.setFieldValue(NEW_REQUEST_FIELD.selectedUser, {
            label: queriedUserFullName,
            userMail: queriedUsername,
            userId: queriedCredentialId,
        });
    }, [queriedUserFullName]);

    const userId = newRequestFormik.values.selectedUser?.userId;
    //const { limitsUsage, isLoading } = useLimitsUsageLogic({ userId, includeArchived: false });
    const { allLimitsUsage, getAllLimits, isLoading } = useUsedLimits({
        userId: isCurrentUserLead ? userId : undefined,
        includeArchived: false,
    });

    useEffect(() => {
        const preSelectedId = (location.state as { preSelectedAbsenceTypeId?: string } | null)
            ?.preSelectedAbsenceTypeId;
        if (!preSelectedId || !allLimitsUsage?.length) return;

        const match = allLimitsUsage.find(({ absenceTypeId }) => absenceTypeId === preSelectedId);
        if (!match) return;

        newRequestFormik.setFieldValue(NEW_REQUEST_FIELD.selectedRequestType, match.absenceType);
        window.history.replaceState({}, '');
    }, [allLimitsUsage]);

    const getRequestSummary = useCallback(() => {
        const { values } = newRequestFormik;
        const { startDate, endDate } = values?.dateRange;
        const dto = prepareRequestBody(values);

        if (!values?.selectedUser || !startDate || !endDate || !values?.selectedRequestType || !dto) return;

        getRequestPreviewWrapper
            .fetch({
                userId: values.selectedUser.userId,
                requestBody: dto,
            })
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('newRequest_alerts_failed_get_summary'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_REQUEST_PREVIEW);
            })
            .onSuccess(response => {
                const requestSummary = convertRequestPreviewResponseDtoToRequestSummary(response);

                setIsLimitExceeded(response?.isOverLimit);
                setRequestSummary(requestSummary);
            });
    }, [createAlert, getRequestPreviewWrapper, newRequestFormik, t]);

    useEffect(getRequestSummary, [
        newRequestFormik?.values?.dateRange,
        newRequestFormik?.values?.selectedRequestType,
        newRequestFormik?.values?.selectedUser,
    ]);

    const onReset = useCallback(() => {
        newRequestFormik?.resetForm();
        newRequestFormik.setValues({
            ...INITIAL_VALUES,
            selectedUser: newRequestFormik.values.selectedUser,
        });
        setReloadDataOnReset([]);
        setIsLimitExceeded(false);
        setRequestSummary(undefined);
        setIsCreatedSuccess(false);
    }, [newRequestFormik]);

    return {
        limitsUsage: allLimitsUsage,
        newRequestFormik,
        isLoading,
        isCreatedSuccess,
        requestSummary,
        isLimitExceeded,
        isSubmitting: createOwnRequestWrapper?.isLoading || createUserRequestWrapper?.isLoading,
        isLoadingSummary: getRequestPreviewWrapper?.isLoading,
        reloadDataOnReset,
        onReset,
    };
};

const prepareRequestBody = (values: NewRequestFields) => {
    if (!values.dateRange.endDate || !values.dateRange.startDate || !values?.selectedRequestType?._id) return;

    return {
        from: normalizeDateForAPI(values.dateRange.startDate),
        to: normalizeDateForAPI(values.dateRange.endDate),
        absenceTypeId: values.selectedRequestType._id,
        comment: values?.comment,
    };
};

export default useNewRequestLogic;
